import { useEffect, useState } from 'react';
import { X, ArrowUpRight } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import { useDomain } from '../../context/DomainContext';
import { getAutonomousApps } from '../../services/appsService';
import logo from '../../assets/logo.png';

const DISMISS_KEY = 'cabazaAppPromoDismissedAt';
const DISMISS_DAYS = 7;

const estMasquee = () => {
    try {
        const at = Number(localStorage.getItem(DISMISS_KEY));
        return !!at && Date.now() - at < DISMISS_DAYS * 24 * 3600 * 1000;
    } catch {
        return false;
    }
};

/**
 * AppPromo - Promotion croisée affichée uniquement dans une app autonome
 * (depenses.cabaza.eu…) : invite à découvrir Cabaza (la plateforme) et à installer
 * les autres apps autonomes. Masquable pour 7 jours.
 */
const AppPromo = () => {
    const { t, language } = useTranslation();
    const { isApp, moduleCode } = useDomain();
    const tp = t.appPromo || {};

    const [apps, setApps] = useState([]);
    const [hidden, setHidden] = useState(estMasquee);

    useEffect(() => {
        if (!isApp || hidden) return undefined;
        let cancelled = false;
        getAutonomousApps(language, moduleCode).then((liste) => { if (!cancelled) setApps(liste); });
        return () => { cancelled = true; };
    }, [isApp, hidden, language, moduleCode]);

    if (!isApp || hidden) return null;

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* stockage indisponible */ }
        setHidden(true);
    };

    return (
        <section aria-label={tp.title || 'Plus d\'applications Cabaza'} className="mt-6 mb-24 sm:mb-6 rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-4 pt-3">
                <h2 className="text-[13px] font-semibold text-gray-500">{tp.title || 'Plus d\'applications Cabaza'}</h2>
                <button type="button" onClick={dismiss} aria-label={tp.dismiss || 'Masquer'} className="w-9 h-9 -mr-2 rounded-xl flex items-center justify-center text-gray-400 hover:bg-gray-100">
                    <X className="w-4 h-4" strokeWidth={1.75} />
                </button>
            </div>

            <ul className="divide-y divide-gray-100">
                <li>
                    <a href="https://www.cabaza.be" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-3 min-h-[64px] hover:bg-gray-50">
                        <img src={logo} alt="" className="h-9 w-auto shrink-0" />
                        <span className="flex-1 min-w-0">
                            <span className="block text-[15px] font-semibold text-gray-900 line-clamp-2">{tp.cabazaTitle || 'Cabaza, le réseau social européen'}</span>
                            <span className="block text-[13px] text-gray-500 line-clamp-2">{tp.cabazaText || 'Contacts, messages, bibliothèque, agenda : tous les modules réunis.'}</span>
                        </span>
                        <span className="flex items-center gap-1 text-sm font-semibold text-violet-700 shrink-0">
                            {tp.discover || 'Découvrir'}<ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                        </span>
                    </a>
                </li>

                {apps.map((app) => (
                    <li key={app.code}>
                        <a href={app.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-3 min-h-[64px] hover:bg-gray-50">
                            {app.iconUrl ? (
                                <img src={app.iconUrl} alt="" className="w-10 h-10 rounded-[22%] object-cover shrink-0" />
                            ) : (
                                <span className="w-10 h-10 rounded-[22%] flex items-center justify-center text-white font-bold shrink-0" style={{ backgroundColor: app.color }}>
                                    {app.name.charAt(0).toUpperCase()}
                                </span>
                            )}
                            <span className="flex-1 min-w-0">
                                <span className="block text-[15px] font-semibold text-gray-900 truncate">{app.name}</span>
                                <span className="block text-[13px] text-gray-500 truncate">{tp.installHint || 'Installez-la sur votre écran d\'accueil'}</span>
                            </span>
                            <span className="flex items-center gap-1 text-sm font-semibold text-violet-700 shrink-0">
                                {tp.open || 'Ouvrir'}<ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                            </span>
                        </a>
                    </li>
                ))}
            </ul>
        </section>
    );
};

export default AppPromo;
